"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Award, CircleCheck as CheckCircle2, Clock, PhoneCall, ShieldCheck, Star, Wrench, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const trustBadges = [{
  icon: ShieldCheck,
  title: "Authorized Brand Experts",
  description: "Factory-trained on American Standard toilets, faucets, and fixture systems."
}, {
  icon: Clock,
  title: "20+ Years Experience",
  description: "Decades of dedicated repair and precision replacement in Jonesboro, GA."
}, {
  icon: Award,
  title: "100% OEM Parts",
  description: "Authentic American Standard replacement cartridges, valves, and seals."
}, {
  icon: Wrench,
  title: "24/7 Rapid Response",
  description: "Fully stocked service vans ready for immediate plumbing emergencies."
}];
const faqs = [{
  question: "Do you use genuine OEM American Standard replacement parts?",
  answer: "Yes. We exclusively install genuine Experienced American Standard cartridges, flappers, flush valves, and seals to protect your warranty and preserve original fixture performance."
}, {
  question: "How fast can you repair an emergency American Standard toilet overflow or leak?",
  answer: "Our certified technicians in Jonesboro are on call 24 hours a day, 7 days a week. We arrive with stocked service trucks to diagnose and resolve fixture emergencies on the spot."
}, {
  question: "Can you upgrade my old toilet to a high-efficiency Champion or Cadet model?",
  answer: "Absolutely. We supply and install the full lineup of American Standard water-saving models including Champion 4 and Cadet 3 series, ensuring proper drain flange fitment and water supply line integration."
}, {
  question: "Are your plumbers certified to work on commercial American Standard fixtures?",
  answer: "Yes, our team is fully licensed and insured in Georgia to service both residential plumbing and commercial American Standard touchless sensor faucets, flushometers, and high-traffic fixtures."
}, {
  question: "What warranties do you provide on American Standard repairs and replacements?",
  answer: "In addition to American Standard's manufacturer warranty, we back every repair and installation with our local Jonesboro 100% satisfaction craftsmanship guarantee."
}];
const testimonials = [{
  name: "Marcus Sterling",
  role: "Jonesboro Homeowner",
  initials: "MS",
  rating: 5,
  comment: "Our American Standard Champion toilet started leaking at 9 PM on a Sunday. The plumber was here within 45 minutes with genuine parts. Flawless repair and fair pricing."
}, {
  name: "Elena Rodriguez",
  role: "Property Manager",
  initials: "ER",
  rating: 5,
  comment: "They handled the replacement of 12 American Standard fixtures across our rental properties. Professional, certified, and left every bathroom spotless."
}];
export default function StandardFaq() {
  const shouldReduceMotion = useReducedMotion();
  const fadeInVariant = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="americanstandardfaqandtrust" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <motion.div initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeInVariant} className="text-center max-w-3xl mx-auto mb-14 sm:mb-20">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wide gap-1.5">
              <Sparkles className="size-3.5 text-accent-foreground" />
              Certified Fixture Specialists
            </Badge>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            American Standard Service, Questions & Trust
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Everything you need to know about certified repair, factory warranties, and 24/7 fixture installation by Jonesboro Emergency Plumber.
          </p>
        </motion.div>

        {/* Trust Badges Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16 sm:mb-24">
          {trustBadges.map((badge, i) => {
          const Icon = badge.icon;
          return <motion.div key={badge.title} data-index={i} initial="hidden" whileInView="show" viewport={{
            once: true,
            amount: 0.2
          }} variants={{
            hidden: {
              opacity: 0,
              y: shouldReduceMotion ? 0 : 20
            },
            show: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.4,
                delay: i * 0.1,
                ease: "easeOut"
              }
            }
          }}>
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200">
                  <CardHeader className="p-6 pb-2">
                    <div className="size-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3">
                      <Icon className="size-6" />
                    </div>
                    <CardTitle className="text-lg font-default font-semibold text-card-foreground">
                      {badge.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6 pt-0">
                    <CardDescription className="text-sm text-muted-foreground leading-normal">
                      {badge.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Main Section: FAQs + Sidebar Trust Card */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* FAQ Accordion (7 cols) */}
          <motion.div initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeInVariant} className="lg:col-span-7 bg-card rounded-2xl p-6 sm:p-8 border border-border shadow-xs">
            <div className="flex items-center gap-2 mb-6">
              <CheckCircle2 className="size-5 text-primary" />
              <h3 className="text-xl font-default font-bold text-foreground">
                Frequently Asked Questions
              </h3>
            </div>

            <Accordion type="single" collapsible defaultValue="item-0" className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={faq.question} value={`item-${i}`} data-index={i} className="border-border">
                  <AccordionTrigger className="text-left font-semibold text-foreground py-4 hover:no-underline hover:text-primary transition-colors">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pt-1 pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </motion.div>

          {/* Sidebar Testimonials & Callout (5 cols) */}
          <motion.div initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeInVariant} className="lg:col-span-5 flex flex-col gap-6">
            {/* Direct Call to Action Card */}
            <Card className="bg-primary text-primary-foreground border-transparent shadow-md overflow-hidden">
              <CardHeader className="p-6 sm:p-8 pb-3">
                <Badge variant="outline" className="bg-primary-foreground/10 text-primary-foreground border-primary-foreground/20 w-fit mb-2">
                  Emergency Dispatch
                </Badge>
                <CardTitle className="text-2xl font-bold font-default text-primary-foreground">
                  Need Fast American Standard Service?
                </CardTitle>
                <CardDescription className="text-primary-foreground/80 mt-2">
                  Our licensed technicians are in your area with OEM parts in stock.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 sm:p-8 pt-2 flex flex-col gap-4">
                <div className="flex items-center gap-3 bg-primary-foreground/10 p-3.5 rounded-lg border border-primary-foreground/15">
                  <Clock className="size-5 text-accent shrink-0" />
                  <span className="text-xs sm:text-sm font-medium">Average local response time: Under 60 mins</span>
                </div>
                <Button asChild size="lg" variant="secondary" className="w-full font-semibold justify-center py-6 text-base shadow-sm">
                  <Link to="/contact/">
                    <PhoneCall className="size-5 mr-2 text-primary" />
                    Request Emergency Plumber
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {/* Testimonials List */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold tracking-wide text-muted-foreground uppercase">
                Verified Local Reviews
              </h4>

              {testimonials.map((test, i) => <Card key={test.name} data-index={i} className="bg-card border-border shadow-2xs hover:shadow-xs transition-[box-shadow] duration-200">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <SafeHydrate><Avatar className="size-9 border border-border">
                          <AvatarFallback className="bg-secondary text-secondary-foreground font-semibold text-xs">
                            {test.initials}
                          </AvatarFallback>
                        </Avatar></SafeHydrate>
                        <div>
                          <div className="text-sm font-semibold text-card-foreground">
                            {test.name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {test.role}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-0.5">
                        {Array.from({
                      length: test.rating
                    }).map((_, starIndex) => <Star key={starIndex} data-index={starIndex} className="size-3.5 fill-accent text-accent" />)}
                      </div>
                    </div>
                    <Separator className="my-2 bg-border/60" />
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed italic">
                      "{test.comment}"
                    </p>
                  </CardContent>
                </Card>)}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}